import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import "../modal.css";
import Autocomplete from "@mui/material/Autocomplete";
import { BASE_URL } from "../../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import { checkIsSuperAdmin } from "../../Utility/utility";
import { InputBase } from "@mui/material";

export default function BusinessModalComponent({ show, handleClose, onSave, editingData, title }) {
  const [businessUnit, setBusinessUnit] = React.useState("");
  const [adminUrl, setAdminUrl] = React.useState("");
  const [businessId, setBusinessId] = React.useState("");
  const [isDefaultChecked, setIsDefaultChecked] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const currentTimestamp = new Date();
  const seconds = currentTimestamp.getSeconds();
  const formattedTimestamp = `${currentTimestamp.getFullYear()}-${(currentTimestamp.getMonth() + 1).toString().padStart(2, "0")}-${currentTimestamp.getDate().toString().padStart(2, "0")} ${currentTimestamp.getHours().toString().padStart(2, "0")}:${currentTimestamp.getMinutes().toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  useEffect(() => {
    const checkAdminStatus = async () => {
      const isSuperAdminUser = await checkIsSuperAdmin();
      setIsSuperAdmin(isSuperAdminUser);

      if (isSuperAdminUser) {
        const orgResponse = await fetch(
          `${BASE_URL}/Organization/${getUserIdFromLocalStorage().userId}/${getUserIdFromLocalStorage().organizationId}`,
          { headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` } }
        );
        if (orgResponse.ok) {
          const orgData = await orgResponse.json();
          setOrganizations(orgData.filter((org) => org && org.id && org.name).reverse());
        }
      } else {
        const userDataJSON = localStorage.getItem("token");
        if (userDataJSON) {
          const userData = JSON.parse(userDataJSON);
          const usersResponse = await fetch(
            `${BASE_URL}/users/getAllUsers/${userData.organizationId}`,
            { headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` } }
          );
          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            setUsers(usersData.filter((user) => user.isDeleted === false));
          }
        }
      }
    };
    checkAdminStatus();
  }, []);

  React.useEffect(() => {
    if (editingData) {
      setBusinessUnit(editingData.businessUnit);
      setAdminUrl(editingData.adminUrl || "");
      setBusinessId(editingData.id);
      setIsDefaultChecked(editingData.isDefault);
      if (isSuperAdmin && editingData.organizationId) {
        const org = organizations.find((o) => o.id === editingData.organizationId);
        if (org) setSelectedOrganization(org);
      } else if (!isSuperAdmin && editingData.userId) {
        const user = users.find((u) => u.id === editingData.userId);
        if (user) setSelectedUser(user);
      }
    } else {
      setBusinessUnit("");
      setAdminUrl("");
      setBusinessId(formattedTimestamp);
      setIsDefaultChecked(false);
      setSelectedOrganization(null);
      setSelectedUser(null);
    }
  }, [editingData, organizations, users, isSuperAdmin]);

  const handleSaveClick = () => {
    const newRow = {
      id: businessId,
      businessUnit,
      adminUrl,
      isDefaultChecked,
      organizationId: isSuperAdmin ? selectedOrganization?.id : null,
      userId: !isSuperAdmin ? selectedUser?.id : null,
    };
    onSave(newRow);
  };

  return (
    <Modal open={show} onClose={handleClose} aria-labelledby="add-administrator-modal-title">
      <div className="user-modal modal-container">
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "24px",
          }}
        >
          <span className="modal-title" style={{ fontWeight: 500 }}>
            {title}
          </span>
          <button onClick={handleClose} className="modal-close-btn" aria-label="Close">
            &#x2715;
          </button>
        </div>

        <div>
          {/* Client Name */}
          <div className="modal-field-wrap">
            <label className="modal-label">Client</label>
            <InputBase
              value={businessUnit}
              className="modal-input"
              onChange={(e) => setBusinessUnit(e.target.value)}
              inputProps={{ style: { padding: 0 } }}
              placeholder="Name"
            />
          </div>

          {/* Admin URL */}
          <div className="modal-field-wrap">
            <label className="modal-label">Admin URL</label>
            <InputBase
              value={adminUrl}
              className="modal-input"
              onChange={(e) => setAdminUrl(e.target.value)}
              inputProps={{ style: { padding: 0 } }}
              placeholder="Enter URL"
            />
          </div>

          {/* Organization (super admin only) */}
          {isSuperAdmin && (
            <div className="modal-field-wrap">
              <label className="modal-label">Organization</label>
              <Autocomplete
                options={organizations}
                getOptionLabel={(option) => option.name || ""}
                value={selectedOrganization}
                onChange={(event, newValue) => setSelectedOrganization(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Select organization..."
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      className: "modal-dropdown-input",
                      style: { width: "100%" },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No organizations found"
                fullWidth
              />
            </div>
          )}

          {/* User (admin only) */}
          {!isSuperAdmin && (
            <div className="modal-field-wrap">
              <label className="modal-label">User</label>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}` || ""}
                value={selectedUser}
                onChange={(event, newValue) => setSelectedUser(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Select User"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      className: "modal-dropdown-input",
                      style: { width: "100%" },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{ padding: "8px 16px" }}>
                    <div>
                      <div
                        style={{ fontSize: "14px" }}
                      >{`${option.firstName} ${option.lastName}`}</div>
                      <div style={{ fontSize: "12px", color: "#666" }}>{option.email}</div>
                    </div>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                noOptionsText="No users found"
                fullWidth
              />
            </div>
          )}

          {/* Buttons */}
          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}
          >
            <Button variant="outlined" onClick={handleClose} className="modal-btn-cancel">
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveClick} className="modal-btn-save">
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

BusinessModalComponent.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingData: PropTypes.object,
  title: PropTypes.string.isRequired,
};
